import { useApolloClient } from "@apollo/client";
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import Cookies from "universal-cookie";
import { AuthError } from "@/lib/auth/errors/auth-error";
import type { AuthClient } from "@/lib/auth/index";

export enum AuthStatus {
  Authenticated = "Authenticated",
  Authenticating = "Authenticating",
  Unauthenticated = "Unauthenticated",
}

enum LoginError {
  INVALID_EMAIL,
  UNKNOWN,
}

export enum VerifyError {
  FAILED_ATTEMPT,
  ATTEMPTS_EXHAUSTED,
}

type AuthState = {
  status: AuthStatus;
  email?: string;
  userId?: string;
  loading: boolean;
  login: {
    loading: boolean;
    type?: "login" | "register";
    error?: LoginError;
  };
  verify: {
    loading: boolean;
    error?: VerifyError;
    attempts: number;
  };
};

type AuthAction =
  | { type: "login/pending" }
  | {
      type: "login/success";
      payload: { email: string; type: "login" | "register" };
    }
  | { type: "login/error"; error: AuthError }
  | { type: "verify/pending" }
  | { type: "verify/success"; payload: { email: string; userId: string } }
  | { type: "verify/error"; error: AuthError }
  | { type: "isAuthenticated/pending" }
  | {
      type: "isAuthenticated/success";
      payload: { email: string; userId: string };
    }
  | { type: "isAuthenticated/error" }
  | { type: "logout" }
  | { type: "reset" };

type TAuthContext = AuthState & {
  authClient?: AuthClient;
  dispatch: Dispatch<AuthAction>;
};

const initialState: AuthState = {
  status: AuthStatus.Unauthenticated,
  loading: true,
  login: {
    loading: false,
  },
  verify: {
    loading: false,
    attempts: 3,
  },
};

const AuthContext = createContext<TAuthContext>({
  ...initialState,
  dispatch: () => void 0,
});

export function useAuthContext() {
  return useContext(AuthContext);
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "isAuthenticated/pending":
      return state;

    case "isAuthenticated/success":
      return {
        ...state,
        loading: false,
        status: AuthStatus.Authenticated,
        email: action.payload.email,
        userId: action.payload.userId,
      };

    case "isAuthenticated/error":
      return {
        ...state,
        loading: false,
        status: AuthStatus.Unauthenticated,
      };

    case "login/pending":
      return {
        ...state,
        status: AuthStatus.Authenticating,
        login: {
          loading: true,
        },
      };

    case "login/success":
      return {
        ...state,
        email: action.payload.email,
        login: {
          loading: false,
          type: action.payload.type,
        },
      };

    case "login/error":
      return {
        ...state,
        login: {
          loading: false,
          error:
            action.error.code === "EmailVerificationException"
              ? LoginError.INVALID_EMAIL
              : LoginError.UNKNOWN,
        },
      };

    case "verify/pending":
      return {
        ...state,
        verify: {
          ...state.verify,
          loading: true,
          error: void 0,
        },
      };

    case "verify/success":
      return {
        ...state,
        email: action.payload.email,
        userId: action.payload.userId,
        status: AuthStatus.Authenticated,
        verify: {
          ...initialState.verify,
        },
      };

    case "verify/error":
      if (
        action.error.code === "ChallengeFailedException" || // thrown on log in
        action.error.code === "CodeMismatchException" // thrown on registration
      ) {
        return {
          ...state,
          status: AuthStatus.Authenticating,
          verify: {
            ...state.verify,
            loading: false,
            attempts:
              state.login.type === "login"
                ? state.verify.attempts - 1
                : state.verify.attempts,
            error: VerifyError.FAILED_ATTEMPT,
          },
        };
      } else if (action.error.code === "NotAuthorizedException") {
        return {
          ...state,
          email: void 0,
          status: AuthStatus.Unauthenticated,
          login: { ...initialState.login },
          verify: {
            ...state.verify,
            error: VerifyError.ATTEMPTS_EXHAUSTED,
            loading: false,
          },
        };
      }

      return {
        ...state,
        status: AuthStatus.Authenticating,
        verify: {
          ...state.verify,
          loading: false,
        },
      };

    case "reset":
    case "logout":
      return {
        ...initialState,
        loading: false,
      };
  }
}

interface AuthProviderProps extends PropsWithChildren {
  authClient: AuthClient;
}

const cookies = new Cookies();

export function AuthProvider({ authClient, children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const isAuthenticated = async () => {
      dispatch({ type: "isAuthenticated/pending" });
      try {
        const { email, sub, token } = await authClient.checkAuth();
        cookies.set("auth-token", token, { path: "/" });
        dispatch({
          type: "isAuthenticated/success",
          payload: {
            email,
            userId: sub,
          },
        });
      } catch {
        dispatch({ type: "isAuthenticated/error" });
      }
    };

    void isAuthenticated();
  }, [authClient]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        authClient,
        dispatch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthLoginOrRegister() {
  const { authClient, dispatch } = useAuthContext();

  if (!authClient) {
    throw new Error("useAuthHooks must be used within an <AuthProvider />");
  }

  const action = useMemo(() => {
    return async ({ email }: { email: string }) => {
      dispatch({
        type: "login/pending",
      });

      try {
        await authClient.login(email);

        dispatch({
          type: "login/success",
          payload: { email, type: "login" },
        });
        return;
      } catch (err) {
        if (err instanceof AuthError) {
          if (err.code === "UserNotFoundException") {
            try {
              await authClient.register(email);
              dispatch({
                type: "login/success",
                payload: { email, type: "register" },
              });
              return;
            } catch (registrationError) {
              dispatch({
                type: "login/error",
                error:
                  registrationError instanceof AuthError
                    ? registrationError
                    : new AuthError(registrationError),
              });
              return;
            }
          }

          dispatch({
            type: "login/error",
            error: err,
          });
          return;
        }

        dispatch({
          type: "login/error",
          error: err instanceof AuthError ? err : new AuthError(err),
        });
      }
    };
  }, [authClient, dispatch]);

  return {
    authLoginOrRegister: action,
  };
}

export function useAuthVerify() {
  const {
    authClient,
    dispatch,
    email,
    login: { type },
  } = useAuthContext();

  if (!authClient) {
    throw new Error("useAuthHooks must be used within an <AuthProvider />");
  }

  const action = useMemo(() => {
    return ({ otpCode }: { otpCode: string }) => {
      if (!email) {
        throw new Error("No email in state!");
      }
      dispatch({ type: "verify/pending" });

      const promise =
        type === "login"
          ? authClient.sendLoginChallenge(otpCode)
          : authClient.confirmRegistration(email, otpCode);

      promise.then(
        () => {
          authClient.checkAuth().then(
            ({ email, sub, token }) => {
              cookies.set("auth-token", token, { path: "/" });
              dispatch({
                type: "verify/success",
                payload: { email, userId: sub },
              });
            },
            (err) => {
              dispatch({
                type: "verify/error",
                error: err instanceof AuthError ? err : new AuthError(err),
              });
            },
          );
        },
        (err) => {
          dispatch({
            type: "verify/error",
            error: err instanceof AuthError ? err : new AuthError(err),
          });
        },
      );
    };
  }, [authClient, dispatch, email, type]);

  return {
    authVerify: action,
  };
}

export function useAuthLogout() {
  const { authClient, dispatch } = useAuthContext();
  const apolloClient = useApolloClient();

  // We don't check for authClient here because this hook can be used on the loading
  // page before the auth context is created

  const action = useMemo(() => {
    return async () => {
      // logout from cognito
      await authClient?.logout();
      cookies.remove("auth-token", { path: "/" });
      dispatch({ type: "logout" });

      // clear apollo cache
      await apolloClient.clearStore();
    };
  }, [apolloClient, authClient, dispatch]);

  return {
    logout: action,
  };
}

export function useAuthReset() {
  const { authClient, dispatch } = useAuthContext();

  if (!authClient) {
    throw new Error("useAuthHooks must be used within an <AuthProvider />");
  }

  const action = useMemo(() => {
    return async () => {
      await authClient.logout();
      cookies.remove("auth-token", { path: "/" });
      dispatch({ type: "logout" });
    };
  }, [authClient, dispatch]);

  return {
    authReset: action,
  };
}

export function useAuthVerifyRetry() {
  const {
    authClient,
    email,
    login: { type },
  } = useAuthContext();

  if (!authClient) {
    throw new Error("useAuthHooks must be used within an <AuthProvider />");
  }

  const action = useMemo(() => {
    return async () => {
      if (!email) {
        throw new Error("no email");
      }

      if (type === "login") {
        await authClient.login(email);
      } else {
        await authClient.resendSignUpCode(email);
      }
    };
  }, [authClient, email, type]);

  return {
    authVerifyRetry: action,
  };
}
